import { Box, Stack, Tab, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useLangContext } from "../../../context/langContext";
import tableInfos from "./receipts-table.json";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { formatDateWithTime } from "../../../utils/date-formatter";
import CustomTable from "../../common/tables/Table";

import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";

import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";

import { routerPaths } from "../../../constants/Paths";

import InfoBadge, { OriginEnum } from "../../common/info-badge/InfoBadge";
import {
  PaymentAdListColumnSortable,
  PaymentAdminListRequest,
} from "../../../services/Endpoints/payments/PaymentAdListEndpoint";
import FilterLinkAction from "../../common/filter-action/FilterLinkAction";
import { EmptyWallet } from "iconsax-react";
import { useUrl } from "../../../hooks/urlEncoding";
import { WalletTransactionListColumnFilterable, WalletTransactionListColumnSortable } from "../../../services/Endpoints/wallet/WalletTransactionListEndpoint";
import UserLink from "../common/UserLink";
import AgentLink from "../common/PaymentAgentLink";
import LinkWithPopover from "../common/LinkWithPopover";
import {
  changeToToman,
  formatCurrency,
} from "../../../utils/currency-formater";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import { ITableSchema } from "../../../utils/types";
import useTableSchema from "../../../hooks/useTableSchema";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import { PaymentVerifyingStatusEnum } from "../../../services/Endpoints/payments/PaymentListEndpoint";
import SelectionFilter from "../../common/tables/filters/SelectionFilter";
import useUrlSort from "../../../hooks/useUrlSort";

interface ITransaction {
  id: number;
  description: string;
  created_at: string;
  amount: number;
  status: ReactNode | string;
  user_id: ReactNode;
  redirect_url: ReactNode | string;
  operations: ReactNode | string;
  payment_gateway_id: ReactNode;
}
export interface ISupportTabsComponent {
  walletReload: boolean;
  setWalletReload: (payload: boolean) => void;
}
const statusOptions = {
  [PaymentVerifyingStatusEnum.PAYING]: "در حال پرداخت",
  [PaymentVerifyingStatusEnum.PAID]: "پرداخت شده",
  [PaymentVerifyingStatusEnum.FAILED]: "پرداخت نشده",
  [PaymentVerifyingStatusEnum.PENDING]: "معلق",
};

export default function ReceiptTableComponent({
  walletReload,
  setWalletReload,
}: Readonly<ISupportTabsComponent>) {
  const { parseQueryString, setParamWithSearchParams } = useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [value, setValue] = useState("1");
  const { lang } = useLangContext();
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [dataTransaction, setDataTransaction] = useState<ITransaction[] | []>(
    []
  );
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const [waiting, setWaiting] = useState(false);
  const { queryStringUrl } = useUrl();

  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const { can } = useCanAccess();
  const location = useLocation();

  const getDataTransaction = async () => {
    try {
      setWaiting(true);
      const walletListRequest = new PaymentAdminListRequest(protectedInstance);
      const resp = await walletListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const transactions = resp.data.payment_receipts.map(
        ({
          id,
          created_at,
          amount,
          status,
          user_id,
          redirect_url,
          payment_gateway_id,
          description,
          user,
          payment_gateway,
        }) => ({
          id,
          created_at,
          amount,
          status: (
            <>
              <InfoBadge
                origin={OriginEnum.PAYMENT}
                status={status as "pending"}
              />
            </>
          ),
          user_id: (
            <UserLink
              disabled={!can(AccessEnums.AdminUsersList)}
              user={{ id: user_id, mobile: user.mobile }}
            />
          ),
          redirect_url: (
            <Typography
              variant="footerRegular"
              component={Link}
              to={redirect_url}
              sx={{
                textDecoration: "none",
              }}
              color={(theme) => theme.palette.others.link}
            >
              {redirect_url}
            </Typography>
          ),
          payment_gateway_id: (
            <AgentLink
              disabled={!can(AccessEnums.AdminPaymentGatewayList)}
              agent={{ id: payment_gateway_id, name: payment_gateway.caption }}
            />
          ),
          description,
          operations: (
            <Stack
              gap="4px"
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <FilterLinkAction link={`/${routerPaths.RECEIPT}/${id ?? 1}`} />
              {status !== "failed" && (
                <LinkWithPopover
                  disabled={!can(AccessEnums.AdminPaymentReceiptsDetails)}
                  title="تراکنش کیف پول"
                >
                  <Stack
                    sx={{
                      padding: "4px",
                      borderRadius: "50%",
                      width: "fit-content",
                      cursor: "pointer",
                      color: (theme) => theme.palette.complement[400],
                      "&:hover": {
                        color: (theme) => theme.palette.others.link,
                        // backgroundColor: (theme) => theme.palette.others.doneBg,
                      },
                    }}
                    component={Link}
                    to={queryStringUrl(routerPaths.WALLET_TRANSACTION, {
                      filters: [
                        {
                          column:
                            WalletTransactionListColumnFilterable.PAYMENT_RECEIPT_ID,
                          operator: FilterOperatorEnum.EQUAL,
                          value: id,
                        },
                      ],
                      sorts: {
                        [WalletTransactionListColumnSortable.ID]: {
                          column: WalletTransactionListColumnSortable.ID,
                          direction: SortDirectionEnum.DESC,
                        },
                      },
                    })}
                  >
                    <EmptyWallet size="20" />
                  </Stack>
                </LinkWithPopover>
              )}
            </Stack>
          ),
        })
      );
      setDataTransaction(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataTransaction();
  }, [walletReload, page, rowsPerPage, value, location.search]);

  const tInfo = tableInfos[lang];
  const colName = tInfo.transactionColumns;

  const _columns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
      sortable : true
    },
    {
      key: "created_at",
      label: colName.created_at,
      minWidth: 100,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) =>
        value && formatDateWithTime({ lang, date: value }),
    },
    {
      key: "amount",
      label: "مبلغ",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
      format: (value) => `${formatCurrency(changeToToman(value))} تومان`,
    },
    {
      key: "user_id",
      label: " کاربر",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "redirect_url",
      label: "لینک",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "payment_gateway_id",
      label: " درگاه",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "status",
      label: "وضعیت",
      minWidth: 170,
      align: "center",
      FilterComponent: SelectionFilter,
      options: statusOptions,
    },
    {
      key: "description",
      label: "توضیحات",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];
  const { filtersSchema, columns } = useTableSchema(_columns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        reloadAction={() => setWalletReload(!walletReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataTransaction}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
    </Box>
  );
}
